import { useCallback, useEffect, useMemo, useState } from "react";

import {
  fetchWorkForCandidates,
  postWorkFor,
  type AuthUser,
  type WorkForCandidate,
} from "@penn/web-common";
import type { FilterOptionOption } from "react-select";
import { PennDropdown, PennText } from "@/components/ui";
import { StageUserModal, type ExistingAccount } from "./StageUserModal";
import { useRegion } from "@/hooks/useRegion";
import { getApiErrorMessage } from "@penn/web-common";
import {
  COMPARE_COST_INVALIDATE,
  dispatchCompareCostInvalidate,
  dispatchWorkForChanged,
} from "@penn/web-common";
import {
  readStoredWorkFor,
  writeStoredWorkFor,
  type StoredWorkFor,
} from "@penn/web-common";

const OTHER_VALUE = "__other__";

/**
 * react-select's default filter hides an option whose LABEL doesn't contain
 * the typed text. The "Other (unregistered email)..." sentinel's label never
 * contains an email address, so typing one (the exact moment it's needed —
 * no registered candidate matches an unregistered address) hid it along with
 * everything else, leaving no option to click at all. Keep it pinned.
 */
function filterWorkForOption(
  option: FilterOptionOption<{ value: string; label: string }>,
  inputValue: string,
): boolean {
  if (option.value === OTHER_VALUE) return true;
  return option.label.toLowerCase().includes(inputValue.trim().toLowerCase());
}

/** Stable identity key so we only reset the Selector on a REAL target change. */
function workForKey(w: StoredWorkFor): string {
  if (w.kind === "registered") return `registered:${w.userId}`;
  if (w.kind === "other") return `other:${w.email.trim().toLowerCase()}`;
  return "self";
}

// POC option label format: "email — name / company" (formatActAsOptionLabel).
// Email first, then the person's name and company joined
// by " / ".
function formatWorkForLabel(
  email: string,
  name: string,
  company: string,
): string {
  const bits: string[] = [];
  if (name.trim()) bits.push(name.trim());
  if (company.trim()) bits.push(company.trim());
  const suffix = bits.length ? ` — ${bits.join(" / ")}` : "";
  return `${email}${suffix}`;
}

type WorkForSelectorProps = {
  selfUserId: string;
  selfEmail: string;
  /** Self name/company so the current user renders in POC "email — name / company" form. */
  selfFirstName?: string | null;
  selfLastName?: string | null;
  selfCompanyName?: string | null;
  enabled: boolean;
  /** `bar` is the single-row form the admin surface band uses. */
  variant?: "menu" | "inline" | "bar";
  /**
   * Fired after a Work-For target is successfully applied. The navbar uses this
   * to reload the effective profile so the on-screen assumptions (labor rate,
   * OEE, …) reflect the newly selected target rather than the caller's own.
   */
  onApplied?: () => void;
};

export function WorkForSelector({
  selfUserId: _selfUserId,
  selfEmail,
  selfFirstName,
  selfLastName,
  selfCompanyName,
  enabled,
  variant = "menu",
  onApplied,
}: WorkForSelectorProps) {
  const [candidates, setCandidates] = useState<WorkForCandidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectValue, setSelectValue] = useState<string>(selfEmail);
  const [stageOpen, setStageOpen] = useState(false);
  const [storedTarget, setStoredTarget] = useState<StoredWorkFor>(() =>
    readStoredWorkFor(),
  );
  // Region-switch staff (engineering) see the candidate list for the region they
  // have selected in the Selector dropdown. The backend ignores this for admin
  // (all regions) and home-only roles (e.g. sales).
  const region = useRegion();

  const syncFromStorage = useCallback(() => {
    const stored = readStoredWorkFor();
    setStoredTarget(stored);
    if (stored.kind === "self") {
      setSelectValue(selfEmail);
      return;
    }
    setSelectValue(stored.email.trim().toLowerCase());
  }, [selfEmail]);

  useEffect(() => {
    if (!enabled) return;
    syncFromStorage();
    setLoading(true);
    fetchWorkForCandidates(region)
      .then((items) => setCandidates(items))
      .catch((e) => setError(getApiErrorMessage(e, "Failed to load customers")))
      .finally(() => setLoading(false));
  }, [enabled, syncFromStorage, region]);

  // Work-For can also be switched programmatically (task-open, R-5) — re-read
  // storage on the same event the banner listens to so the select stays in sync.
  useEffect(() => {
    window.addEventListener(COMPARE_COST_INVALIDATE, syncFromStorage);
    return () =>
      window.removeEventListener(COMPARE_COST_INVALIDATE, syncFromStorage);
  }, [syncFromStorage]);

  const options = useMemo(() => {
    const rows: Array<{ value: string; label: string }> = [];
    const seen = new Set<string>();
    const add = (
      email: string,
      name: string,
      company: string,
      isSelf = false,
    ) => {
      const key = email.trim().toLowerCase();
      if (!key || seen.has(key)) return;
      seen.add(key);
      const base = formatWorkForLabel(email.trim(), name, company);
      // The current user's own row is prefixed "Myself" so it is easy to tell
      // apart from the customer candidates.
      rows.push({ value: key, label: isSelf ? `Myself — ${base}` : base });
    };
    // The current user appears in the same list + format (POC parity).
    add(
      selfEmail,
      [selfFirstName, selfLastName].filter(Boolean).join(" "),
      selfCompanyName ?? "",
      true,
    );
    for (const c of candidates) {
      const nm = [c.firstName, c.lastName].filter(Boolean).join(" ").trim();
      add(c.email, nm, c.companyName ?? "");
    }
    // Someone just registered through the Work-For flow is the active target
    // before this list refetches (the nav bar never unmounts). `add` dedupes,
    // so this only fills a gap — without it the select holds a value it has no
    // option for and renders blank.
    if (storedTarget.kind === "registered") {
      add(
        storedTarget.email,
        storedTarget.name ?? "",
        storedTarget.company ?? "",
      );
    }
    // An unregistered-email target has no candidate row of its own.
    if (storedTarget.kind === "other") {
      add(storedTarget.email, "", "(unregistered)");
    }
    // Sort the list by email (POC parity).
    rows.sort((a, b) => a.value.localeCompare(b.value));
    // Act for an unregistered email (MVP addition; POC had no register-others).
    rows.push({ value: OTHER_VALUE, label: "Other (unregistered email)..." });
    return rows;
  }, [
    candidates,
    selfEmail,
    selfFirstName,
    selfLastName,
    selfCompanyName,
    storedTarget,
  ]);

  const applySelection = async (stored: StoredWorkFor): Promise<boolean> => {
    setError(null);
    const changed = workForKey(readStoredWorkFor()) !== workForKey(stored);
    try {
      if (stored.kind === "self") {
        await postWorkFor({});
        writeStoredWorkFor({ kind: "self" });
      } else if (stored.kind === "registered") {
        await postWorkFor({ userId: stored.userId });
        writeStoredWorkFor(stored);
      } else {
        await postWorkFor({ email: stored.email });
        writeStoredWorkFor(stored);
      }
      dispatchCompareCostInvalidate();
      // Manually switching the customer resets the Selector's filter workspace
      // (fresh slate for the new target). Only on a REAL change so re-picking
      // the same person doesn't wipe in-progress filters.
      if (changed) dispatchWorkForChanged();
      // Reload the effective profile so the target's saved assumptions replace
      // the caller's on screen (Work-For "effective user" parity).
      onApplied?.();
      return true;
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to update Work-For"));
      syncFromStorage();
      return false;
    }
  };

  const onSelectChange = async (value: string) => {
    setSelectValue(value);
    if (value === OTHER_VALUE) {
      setError(null);
      setStageOpen(true);
      return;
    }
    const match = candidates.find(
      (c) => c.email.trim().toLowerCase() === value.trim().toLowerCase(),
    );
    if (value.trim().toLowerCase() === selfEmail.trim().toLowerCase()) {
      await applySelection({ kind: "self" });
      return;
    }
    if (match?.userId) {
      await applySelection({
        kind: "registered",
        userId: match.userId,
        email: match.email,
        name: [match.firstName, match.lastName]
          .filter(Boolean)
          .join(" ")
          .trim(),
        company: match.companyName ?? "",
      });
      return;
    }
    if (match && !match.isRegistered) {
      await applySelection({ kind: "other", email: match.email });
      return;
    }
    setError("Select a registered user or choose Other.");
  };

  /**
   * What an email that already has an account means here. Reads a fresh
   * candidate list: the one loaded at mount misses anyone staged since.
   */
  const resolveRegistered = async (
    rawEmail: string,
  ): Promise<ExistingAccount> => {
    const email = rawEmail.trim().toLowerCase();
    if (email === selfEmail.trim().toLowerCase()) return { kind: "self" };
    let list = candidates;
    try {
      list = await fetchWorkForCandidates(region);
      setCandidates(list);
    } catch {
      /* fall back to the list already loaded */
    }
    // A registered person must travel as a userId (the backend rejects a
    // registered email sent as "other" with 422 work-for-use-user-id).
    const match = list.find(
      (c) => c.email.trim().toLowerCase() === email && c.userId,
    );
    return match?.userId
      ? { kind: "candidate", candidate: match }
      : { kind: "blocked" };
  };

  /** Switch Work-For to an existing account the dialog resolved. */
  const selectExisting = async (
    existing: ExistingAccount,
  ): Promise<boolean> => {
    if (existing.kind === "self") return applySelection({ kind: "self" });
    if (existing.kind !== "candidate") return false;
    const c = existing.candidate;
    return applySelection({
      kind: "registered",
      userId: c.userId as string,
      email: c.email,
      name: [c.firstName, c.lastName].filter(Boolean).join(" ").trim(),
      company: c.companyName ?? "",
    });
  };

  const selectStagedCustomer = async (user: AuthUser): Promise<void> => {
    await applySelection({
      kind: "registered",
      userId: user.id,
      email: user.email,
      name: [user.firstName, user.lastName].filter(Boolean).join(" ").trim(),
      company: user.companyName ?? "",
    });
  };

  const closeStage = () => {
    setStageOpen(false);
    // Put the dropdown back on whoever is actually active.
    syncFromStorage();
  };

  if (!enabled) return null;

  const isMenu = variant === "menu";
  const isBar = variant === "bar";

  return (
    <div
      className={
        isMenu
          ? "px-4 py-3 border-b border-gray-100"
          : isBar
            ? "flex items-center gap-2 min-w-0"
            : "flex w-full flex-col gap-1"
      }
      onClick={(e) => e.stopPropagation()}
    >
      <PennText
        as="label"
        size="xs"
        color={isBar ? "white" : "blue_grey"}
        font="semibold"
        className={
          isMenu
            ? "uppercase text-slate-500"
            : isBar
              ? "uppercase whitespace-nowrap hidden md:inline"
              : "text-slate-600"
        }
      >
        {isMenu || isBar ? "WORKING FOR" : "I want to work as..."}
      </PennText>
      {isMenu || isBar ? (
        // Native select in the band and the user menu: both are short, fixed
        // slots where a 28px-high combobox has nowhere to open.
        <select
          value={selectValue}
          disabled={loading}
          onChange={(e) => void onSelectChange(e.target.value)}
          className={
            isMenu
              ? "mt-2 h-9 w-full rounded-lg border border-slate-300 bg-white px-2 text-sm font-semibold text-slate-900 truncate focus:outline-none"
              : "h-7 max-w-[260px] sm:max-w-[340px] rounded-md border border-slate-600 bg-slate-800 px-2 text-xs font-semibold text-slate-100 truncate focus:outline-none focus:ring-1 focus:ring-amber-300"
          }
          title="Customer you are working for"
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      ) : (
        // Type-to-filter: the list is region-wide and capped at 500 rows, so
        // scrolling a plain dropdown to a name is not realistic.
        <PennDropdown
          className="work-for-picker w-full"
          inputId="work-for-customer"
          name="workForCustomer"
          options={options}
          value={selectValue}
          onChange={(value) => void onSelectChange(value)}
          filterOption={filterWorkForOption}
          placeholder="Search customers by name, company or email…"
          isSearchable
          isDisabled={loading}
          isLoading={loading}
        />
      )}
      <StageUserModal
        open={stageOpen}
        onClose={closeStage}
        resolveRegistered={resolveRegistered}
        onSelectExisting={selectExisting}
        onStagedCustomer={selectStagedCustomer}
      />
      {error && (
        <PennText as="p" size="xs" color="error" className="mt-1">
          {error}
        </PennText>
      )}
    </div>
  );
}
